import classnames from 'classnames';
import React from 'react';
import { FormGroup } from 'react-bootstrap';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { connect } from 'react-redux';
import {
  hasPermission,
  EXTERNAL_SEARCHER_PERMISSIONS,
  REVIEWER_PERMISSIONS,
} from 'v2/utils/permissions';
import { GenericObject, Permission } from 'v2/models';
import { CREATE_SEARCH_DESCRIPTION_LIMIT } from 'utils/constants';
import Checkbox from 'v2/form/Checkbox/Checkbox';
import Input from 'v2/form/Input/Input';
import TextArea from 'v2/form/TextArea/TextArea';
import { CreateSearchFormData } from '../CreateSearchSchema';
import DescriptionGuidance from './DescriptionGuidance/DescriptionGuidance';
import CheckDescription, { DescriptionType } from './CheckDescription';
import styles from './basicInformationStep.module.scss';
import { DataProductFormData } from 'v2/DataProducts/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import { useFreeUser } from 'v2/hooks/useFreeUser';

interface StateProps {
  permissions: Record<string, Permission>;
}

interface BasicInformationStepProps {
  control: Control<CreateSearchFormData>;
  register: UseFormRegister<CreateSearchFormData>;
  errors: FieldErrors<CreateSearchFormData>;
  data: CreateSearchFormData;
  showModal: boolean;
  onConfirmModal: () => void;
  onCancelModal: () => void;
}

const BasicInformationStep = ({
  register,
  control,
  errors,
  data,
  showModal,
  onConfirmModal,
  onCancelModal,
  permissions,
}: BasicInformationStepProps & StateProps) => {
  const [isFreeUser] = useFreeUser();
  const isExternalSearch = hasPermission(
    permissions,
    EXTERNAL_SEARCHER_PERMISSIONS
  );
  const isReviewer = hasPermission(permissions, REVIEWER_PERMISSIONS);
  const convertedControl = control as Control<
    CreateSearchFormData | DataProductFormData
  >;
  const checkDescriptionType =
    isExternalSearch || isReviewer
      ? DescriptionType.REQUEST
      : DescriptionType.INTERNAL_REQUEST;

  return (
    <div className={styles.root}>
      <p className="text-light8 pt-5">
        Please name your request and provide a detailed description of the data
        you are looking for.
      </p>
      <FormGroup className="mt-4">
        <Input
          label="Request Name"
          type="text"
          register={register('name', { required: true })}
          error={errors?.name?.message}
          maxLength={64}
          autoComplete="off"
          required={true}
          data-cy="CreateRequest:request-name"
        />
      </FormGroup>
      <FormGroup className="mt-5 pt-2 position-relative">
        <DescriptionGuidance error={errors?.description?.message} />
        <TextArea
          label=""
          register={register('description', { required: true })}
          error={errors?.description?.message}
          rows={8}
          autoComplete="off"
          required={true}
          maxLength={CREATE_SEARCH_DESCRIPTION_LIMIT}
          data-cy="CreateRequest:request-description"
        />
        <small className={classnames('text-light8', styles.characterCount)}>
          {data.description.length} of {CREATE_SEARCH_DESCRIPTION_LIMIT}{' '}
          characters used
        </small>
      </FormGroup>

      <CheckDescription
        control={convertedControl}
        errors={errors}
        type={checkDescriptionType}
        hideDescriptionForBadStatus={isFreeUser}
      />

      {isFreeUser ? null : (
        <FormGroup className="mt-5">
          <Checkbox
            label="Identity"
            register={register('hide_identity')}
            error={errors?.hide_identity?.message}
            text="Hide My Identity"
          />
          <p className="text-light8 font-italic mt-2">
            Hiding your identity may reduce the number of results you receive.
          </p>
        </FormGroup>
      )}
      <ConfirmationModal
        centered={true}
        animation={true}
        show={showModal}
        onCancel={onCancelModal}
        okBtnText="Yes, Proceed"
        onOk={onConfirmModal}
        title={'Are you sure?'}
      >
        <div>
          <p>
            Your request is not well formed. If you don’t improve it you aren’t
            likely to get affirmative responses from data vendors.
          </p>
          <p>
            <b>
              Are you sure you want to proceed without improving your
              description?
            </b>
          </p>
        </div>
      </ConfirmationModal>
    </div>
  );
};

const mapStateToProps = (state: GenericObject) => {
  return {
    permissions: state.permissions.permissions,
  };
};

export default connect<StateProps>(mapStateToProps)(BasicInformationStep);
